import useSWRMutation from 'swr/mutation';
import urlResolver from 'edrolo/utils/URLResolver';
import Cookies from 'js-cookie';
import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import { useAnalytics } from 'edrolo/analytics';
import waffle from 'edrolo/utils/Waffle';

async function createTaskRequest(key: string) {
  const csrftoken: string = Cookies.get('csrftoken') || '';

  return fetch(key, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrftoken,
    },
  });
}

const useCreateTask = () => {
  const { trigger } = useSWRMutation(
    urlResolver.reverse('api:tasks'),
    createTaskRequest
  );

  return {
    createTask: () => trigger(),
  };
};

interface Options {
  courseId: CourseId | undefined;
  classId: ClassId | undefined;
  topicId: TopicId | undefined;
  lessonId: LessonId | undefined;
  examId: ExamId | undefined;
}

export const useCreateAndNavigateToTask = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { createTask } = useCreateTask();
  const history = useHistory();
  const { track } = useAnalytics({});

  async function createAndNavigateToTask(options?: Partial<Options>) {
    // Tasks 2.0: route straight into the wizard with whatever course/class/
    // topic/lesson context is available preselected, instead of creating a
    // bare task and jumping into its (now-defunct) legacy content browser.
    // This must be a full-page navigation, not a react-router push, because
    // the wizard lives in a separate Vite bundle outside this SPA's route
    // tree — same reasoning as TasksRebuildRedirect.
    if (waffle.flag_is_active('tasks-rebuild')) {
      const urlSearchParams = new URLSearchParams({
        ...(options?.courseId !== undefined && {
          courseId: `${options.courseId}`,
        }),
        ...(options?.classId !== undefined && {
          classId: `${options.classId}`,
        }),
        ...(options?.topicId !== undefined && {
          topicId: `${options.topicId}`,
        }),
        ...(options?.lessonId !== undefined && {
          lessonId: `${options.lessonId}`,
        }),
      });
      window.location.assign(
        `/home/tasks/new/1/?${urlSearchParams.toString()}`
      );
      return;
    }

    setIsLoading(true);

    const response = await createTask();
    const task = await response.json();

    const urlSearchParams = new URLSearchParams({
      ...(options?.courseId && { courseIds: `${options.courseId}` }),
      ...(options?.topicId && { topicIds: `${options.topicId}` }),
      ...(options?.examId && { examIds: `${options.examId}` }),
    });

    const url = `${urlResolver.reverse('task-page', {
      taskId: task.id,
    })}?${urlSearchParams.toString()}`;

    track('Create Task - Started', {
      task_id: task.id,
    });

    setIsLoading(false);
    history.push(url);
  }

  return {
    createAndNavigateToTask,
    isLoading,
  };
};
